/**
 * SettingsPage — Household settings (primary users only).
 * - Number of recipes per selection page
 * - Select 1-3 grocery stores
 * - Day of week to reset selections
 * - How often to reset (weekly/biweekly/monthly)
 *
 * Persists to DynamoDB via:
 *   GET  /v1/households/{hhId}          → loads settings
 *   PUT  /v1/households/{hhId}/settings → saves settings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Store, Calendar, Hash, Loader, CheckCircle } from 'lucide-react';
import { SUPPORTED_STORES } from '@tabletryb/shared';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface HouseholdSettings {
  mealsPerSelection: number;
  resetDay: number;
  resetFrequency: 'weekly' | 'biweekly' | 'monthly';
  groceryStoreIds: string[];
  dietaryPreferences: string[];
  excludedIngredients: string[];
  defaultServings: number;
}

const DEFAULT_SETTINGS: HouseholdSettings = {
  mealsPerSelection: 20,
  resetDay: 5,
  resetFrequency: 'weekly',
  groceryStoreIds: [],
  dietaryPreferences: [],
  excludedIngredients: [],
  defaultServings: 4,
};

const SettingsPage: React.FC = () => {
  const api = useApi();
  const { user } = useAuth();
  const householdId = user?.householdId;

  // Form state
  const [mealsPerSelection, setMealsPerSelection] = useState(20);
  const [resetDay, setResetDay] = useState(5);
  const [resetFrequency, setResetFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [selectedStores, setSelectedStores] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Track saved state to detect changes
  const [savedSettings, setSavedSettings] = useState<HouseholdSettings>(DEFAULT_SETTINGS);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load settings on mount
  useEffect(() => {
    if (!householdId) {
      setLoading(false);
      return;
    }

    const loadSettings = async () => {
      try {
        const data = await api.get<{
          settings?: HouseholdSettings;
        }>(`/v1/households/${householdId}`);

        const settings = data.settings || DEFAULT_SETTINGS;
        setMealsPerSelection(settings.mealsPerSelection ?? 20);
        setResetDay(settings.resetDay ?? 5);
        setResetFrequency(settings.resetFrequency ?? 'weekly');
        setSelectedStores(settings.groceryStoreIds ?? []);
        setSavedSettings(settings);
      } catch (err) {
        console.error('Failed to load settings:', err);
        showToast('Failed to load settings', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
    
  }, [householdId]);

  // Track changes
  useEffect(() => {
    const changed =
      mealsPerSelection !== (savedSettings.mealsPerSelection ?? 20) ||
      resetDay !== (savedSettings.resetDay ?? 5) ||
      resetFrequency !== (savedSettings.resetFrequency ?? 'weekly') ||
      JSON.stringify(selectedStores) !== JSON.stringify(savedSettings.groceryStoreIds ?? []);
    setHasChanges(changed);
  }, [mealsPerSelection, resetDay, resetFrequency, selectedStores, savedSettings]);

  const toggleStore = (storeId: string) => {
    if (selectedStores.includes(storeId)) {
      setSelectedStores(selectedStores.filter((s) => s !== storeId));
    } else if (selectedStores.length < 3) {
      setSelectedStores([...selectedStores, storeId]);
    }
  };

  const handleSave = async () => {
    if (!householdId) return;
    setSaving(true);
    try {
      const payload = {
        mealsPerSelection,
        resetDay,
        resetFrequency,
        groceryStoreIds: selectedStores,
      };
      await api.put(`/v1/households/${householdId}/settings`, payload);

      // Update saved state so hasChanges resets
      setSavedSettings((prev) => ({
        ...prev,
        ...payload,
      }));
      showToast('Settings saved!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      showToast('Failed to save settings. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <Loader size={24} className="spin" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title"><Settings size={24} /> Settings</h1>

      {/* Toast notification */}
      {toast && (
        <div className={`toast toast--${toast.type}`}>
          {toast.type === 'success' && <CheckCircle size={16} />}
          {toast.message}
        </div>
      )}

      <div className="settings-card">
        {/* Meals per selection */}
        <div className="settings-section">
          <h3><Hash size={18} /> Selection Size</h3>
          <p className="settings-desc">How many recipes to randomly include on the voting page each cycle.</p>
          <div className="settings-control">
            <input
              type="range" min={5} max={30} step={1}
              value={mealsPerSelection}
              onChange={(e) => setMealsPerSelection(Number(e.target.value))}
              className="settings-range"
            />
            <span className="settings-range-value">{mealsPerSelection} recipes</span>
          </div>
        </div>

        {/* Reset schedule */}
        <div className="settings-section">
          <h3><Calendar size={18} /> Reset Schedule</h3>
          <p className="settings-desc">When the selections refresh with new random recipes from your list.</p>
          <div className="settings-row">
            <div className="form-group">
              <label className="form-label">Reset Day</label>
              <select
                className="form-input form-select"
                value={resetDay}
                onChange={(e) => setResetDay(Number(e.target.value))}
              >
                {DAYS.map((day, i) => <option key={i} value={i}>{day}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Frequency</label>
              <select
                className="form-input form-select"
                value={resetFrequency}
                onChange={(e) => setResetFrequency(e.target.value as typeof resetFrequency)}
              >
                <option value="weekly">Every week</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grocery stores */}
        <div className="settings-section">
          <h3><Store size={18} /> Grocery Stores</h3>
          <p className="settings-desc">Select up to 3 stores for grocery list search links.</p>
          <div className="store-picker">
            {SUPPORTED_STORES.map((store) => (
              <button
                key={store.storeId}
                className={`store-chip ${selectedStores.includes(store.storeId) ? 'store-chip--selected' : ''}`}
                onClick={() => toggleStore(store.storeId)}
                disabled={!selectedStores.includes(store.storeId) && selectedStores.length >= 3}
              >
                {store.displayName}
              </button>
            ))}
          </div>
          <p className="settings-hint">
            {selectedStores.length}/3 selected
            {selectedStores.length === 0 && ' — choose your stores to see search links on your grocery list'}
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? (
            <>
              <Loader size={16} className="spin" /> Saving...
            </>
          ) : (
            <>
              <Save size={16} /> {hasChanges ? 'Save Settings' : 'Settings Saved'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
